import { useMutation } from '@apollo/client';
import type { Topic, UpsertTopicInput } from '@communities/content-management/domain';
import { UPSERT_COMMUNITY_TOPIC, GET_COMMUNITY_TOPICS } from '@communities/content-management/graphql';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { useCallback, useState } from 'react';

interface UpsertCommunityTopicResponse {
  upsertCommunityTopic: Topic;
}

export function useUpsertCommunityTopic(communityId?: string) {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [upsertTopicMutation, { loading }] = useMutation<UpsertCommunityTopicResponse>(UPSERT_COMMUNITY_TOPIC, {
    refetchQueries: communityId ? [{ query: GET_COMMUNITY_TOPICS, variables: { communityId } }] : [],
    awaitRefetchQueries: true,
  });

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const upsertTopic = async (communityId: string, topicName: string, input: UpsertTopicInput): Promise<boolean> => {
    try {
      track('UpsertCommunityTopicInitiated', {
        communityId,
        topicName,
      });

      await upsertTopicMutation({
        variables: {
          communityId,
          topicName,
          input,
        },
      });

      track('UpsertCommunityTopicSucceeded', {
        communityId,
        topicName,
      });

      return true;
    } catch (error) {
      track('UpsertCommunityTopicFailed', {
        communityId,
        topicName,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setErrors(transformErrors(error));

      return false;
    }
  };

  return {
    upsertTopic,
    loading,
    errors,
    resetErrors,
  };
}

export default useUpsertCommunityTopic;
